// Associate Dashboard media flywheel: admin-managed promotional/informational
// media (images, PDFs, YouTube/Vimeo/direct-video links) auto-rotating on the
// Associate Dashboard overview. Read-only, associate-facing. Admin management
// lives in AdminFlywheelTab.tsx. Built on the existing (previously unused in
// a live dashboard) Embla carousel primitives in components/ui/carousel.tsx,
// so no new dependency is required.

import { useEffect, useRef, useState, type KeyboardEvent } from "react";
import { trpc } from "@/lib/trpc";
import { Carousel, CarouselContent, CarouselItem, type CarouselApi } from "@/components/ui/carousel";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { ChevronLeft, ChevronRight, ExternalLink, FileText, Loader2, Sparkles } from "lucide-react";

const AUTO_ADVANCE_MS = 10_000;
// Fixed viewport shape for every slide (image, PDF, or video) so a single
// oversized or portrait-orientation asset can't blow out the card height and
// unbalance the dashboard layout. object-contain (below) still shows the
// complete asset without distortion; a wide/short letterbox is preferred
// over an unpredictable card height.
const MEDIA_ASPECT = 16 / 9;
// Long descriptions past roughly this length won't fit the 2-line clamp, so
// only show the toggle when it's actually needed.
const DESCRIPTION_CLAMP_THRESHOLD = 90;
const DEFAULT_HEADING = "What's New";
const DEFAULT_SUBHEADING = "Resources, promos, and updates from BizConnect360.";

function usePrefersReducedMotion(): boolean {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mql = window.matchMedia("(prefers-reduced-motion: reduce)");
    setReduced(mql.matches);
    const onChange = () => setReduced(mql.matches);
    mql.addEventListener?.("change", onChange);
    return () => mql.removeEventListener?.("change", onChange);
  }, []);
  return reduced;
}

function extractYouTubeId(url: string): string | null {
  const m = url.match(/(?:youtube\.com\/watch\?v=|youtube\.com\/shorts\/|youtu\.be\/)([\w-]{6,})/i);
  return m ? m[1] : null;
}
function extractVimeoId(url: string): string | null {
  const m = url.match(/vimeo\.com\/(?:video\/)?(\d+)/i);
  return m ? m[1] : null;
}

export type FlywheelItemRow = {
  id: number;
  title: string;
  description?: string | null;
  mediaUrl: string;
  mediaType: string;
  clickThroughUrl?: string | null;
};

function UnsupportedMedia() {
  return (
    <div className="w-full h-full flex flex-col items-center justify-center gap-2 text-white/40">
      <Sparkles className="w-7 h-7" />
      <span className="text-xs">Media unavailable</span>
    </div>
  );
}

function FlywheelSlideMedia({
  item,
  onEnlargeImage,
  onVideoPlayingChange,
}: {
  item: FlywheelItemRow;
  onEnlargeImage: () => void;
  onVideoPlayingChange: (playing: boolean) => void;
}) {
  const { mediaType, mediaUrl, title } = item;

  if (!mediaUrl) return <UnsupportedMedia />;

  if (mediaType === "image") {
    return (
      <img
        src={mediaUrl}
        alt={title}
        loading="lazy"
        className="w-full h-full object-contain cursor-zoom-in"
        role="button"
        tabIndex={0}
        onClick={onEnlargeImage}
        onKeyDown={(e: KeyboardEvent<HTMLImageElement>) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onEnlargeImage();
          }
        }}
        aria-label={`Enlarge image: ${title}`}
      />
    );
  }

  if (mediaType === "pdf") {
    // PDFs open in a new tab at native/full size rather than an in-page
    // iframe. Several PDF hosts in this project (e.g. DocuSeal) send
    // X-Frame-Options that block inline embedding, so a real link is the
    // reliable "view at a useful size" affordance for this media type.
    return (
      <a
        href={mediaUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="w-full h-full flex flex-col items-center justify-center gap-2 text-white/70 hover:text-white transition-colors"
        aria-label={`Open PDF in a new tab: ${title}`}
      >
        <FileText className="w-10 h-10" />
        <span className="text-sm font-semibold">View PDF</span>
      </a>
    );
  }

  if (mediaType === "youtube") {
    const videoId = extractYouTubeId(mediaUrl);
    if (!videoId) return <UnsupportedMedia />;
    return (
      <iframe
        src={`https://www.youtube.com/embed/${videoId}`}
        title={title}
        className="w-full h-full"
        frameBorder="0"
        allow="clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
      />
    );
  }

  if (mediaType === "vimeo") {
    const videoId = extractVimeoId(mediaUrl);
    if (!videoId) return <UnsupportedMedia />;
    return (
      <iframe
        src={`https://player.vimeo.com/video/${videoId}`}
        title={title}
        className="w-full h-full"
        frameBorder="0"
        allow="fullscreen; picture-in-picture"
        allowFullScreen
      />
    );
  }

  if (mediaType === "video_url") {
    // No autoplay attribute: playback (and audio) only starts on explicit
    // user action via the native controls.
    return (
      <video
        src={mediaUrl}
        controls
        preload="metadata"
        className="w-full h-full object-contain"
        onPlay={() => onVideoPlayingChange(true)}
        onPause={() => onVideoPlayingChange(false)}
        onEnded={() => onVideoPlayingChange(false)}
      />
    );
  }

  return <UnsupportedMedia />;
}

// Prev/next buttons + dot indicators, driven by the parent's Embla `api`
// instance (passed down rather than read via carousel.tsx's internal,
// unexported useCarousel() context hook).
function FlywheelControls({
  api,
  count,
  selected,
}: {
  api: CarouselApi | undefined;
  count: number;
  selected: number;
}) {
  if (count < 2) return null;

  return (
    <>
      <button
        type="button"
        onClick={() => api?.scrollPrev()}
        disabled={!api?.canScrollPrev()}
        aria-label="Previous slide"
        className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-black/45 hover:bg-black/65 text-white flex items-center justify-center disabled:opacity-30 transition-colors"
      >
        <ChevronLeft className="w-4 h-4" />
      </button>
      <button
        type="button"
        onClick={() => api?.scrollNext()}
        disabled={!api?.canScrollNext()}
        aria-label="Next slide"
        className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-black/45 hover:bg-black/65 text-white flex items-center justify-center disabled:opacity-30 transition-colors"
      >
        <ChevronRight className="w-4 h-4" />
      </button>
      <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex items-center gap-1.5" role="tablist" aria-label="Flywheel slides">
        {Array.from({ length: count }).map((_, i) => (
          <button
            key={i}
            type="button"
            role="tab"
            aria-selected={i === selected}
            aria-label={`Go to slide ${i + 1} of ${count}`}
            onClick={() => api?.scrollTo(i)}
            className={`h-1.5 rounded-full transition-all ${i === selected ? "w-5 bg-white" : "w-1.5 bg-white/50 hover:bg-white/70"}`}
          />
        ))}
      </div>
    </>
  );
}

export function FlywheelCarousel() {
  const { data, isLoading, isError } = trpc.flywheel.list.useQuery();
  const items = (data ?? []) as FlywheelItemRow[];
  const headingQuery = trpc.appSettings.get.useQuery({ key: "flywheel_heading" });
  const subheadingQuery = trpc.appSettings.get.useQuery({ key: "flywheel_subheading" });
  const heading = headingQuery.data || DEFAULT_HEADING;
  const subheading = subheadingQuery.data || DEFAULT_SUBHEADING;

  const [api, setApi] = useState<CarouselApi>();
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [isInteracting, setIsInteracting] = useState(false);
  const [videoPlaying, setVideoPlaying] = useState(false);
  const [enlargedItem, setEnlargedItem] = useState<FlywheelItemRow | null>(null);
  const [descExpanded, setDescExpanded] = useState(false);
  const prefersReducedMotion = usePrefersReducedMotion();
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // A description expanded on one slide shouldn't stay expanded after
  // auto-advance or manual navigation moves to the next slide.
  useEffect(() => {
    setDescExpanded(false);
  }, [selectedIndex]);

  // Track the current slide index for the caption + active dot indicator.
  useEffect(() => {
    if (!api) return;
    const onSelect = () => setSelectedIndex(api.selectedScrollSnap());
    onSelect();
    api.on("select", onSelect);
    api.on("reInit", onSelect);
    return () => {
      api.off("select", onSelect);
      api.off("reInit", onSelect);
    };
  }, [api]);

  // Auto-advance ~every 10s. Paused while: reduced motion is requested, the
  // user is actively hovering/focusing/touching the carousel, a video slide
  // is playing, the lightbox is open, or there's nothing to advance to.
  useEffect(() => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    if (!api) return;
    if (prefersReducedMotion || isInteracting || videoPlaying || enlargedItem) return;
    if (items.length < 2) return;
    intervalRef.current = setInterval(() => {
      if (api.canScrollNext()) api.scrollNext();
      else api.scrollTo(0);
    }, AUTO_ADVANCE_MS);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [api, prefersReducedMotion, isInteracting, videoPlaying, enlargedItem, items.length]);

  const pause = () => setIsInteracting(true);
  const resume = () => setIsInteracting(false);
  const current = items[selectedIndex] ?? items[0];
  const descriptionNeedsToggle = (current?.description?.length ?? 0) > DESCRIPTION_CLAMP_THRESHOLD;

  return (
    <div className="bg-white border border-[#EBE2D4] rounded-[18px] shadow-sm p-5 flex flex-col h-full">
      <div className="text-[14px] font-bold text-[#2C2723] mb-1" style={{ fontFamily: "'Spectral', serif" }}>{heading}</div>
      <div className="text-[11px] text-[#9C9285] mb-3">{subheading}</div>

      {isLoading ? (
        <div className="w-full aspect-[16/9] rounded-xl bg-gray-50 border border-gray-100 flex items-center justify-center">
          <Loader2 className="w-6 h-6 text-gray-300 animate-spin" />
        </div>
      ) : isError ? (
        <div className="w-full aspect-[16/9] rounded-xl bg-gray-50 border border-dashed border-gray-200 flex flex-col items-center justify-center gap-1.5 text-center px-4">
          <Sparkles className="w-7 h-7 text-gray-300" />
          <p className="text-xs text-gray-400">Couldn't load updates right now. Try refreshing the page.</p>
        </div>
      ) : items.length === 0 ? (
        <div className="w-full aspect-[16/9] rounded-xl bg-gray-50 border border-dashed border-gray-200 flex flex-col items-center justify-center gap-1.5 text-center px-4">
          <Sparkles className="w-7 h-7 text-gray-300" />
          <p className="text-sm font-medium text-[#6E665A]">Nothing new right now</p>
          <p className="text-xs text-gray-400">Check back soon for resources and updates.</p>
        </div>
      ) : (
        <div
          className="flex-1 flex flex-col justify-center"
          onMouseEnter={pause}
          onMouseLeave={resume}
          onFocus={pause}
          onBlur={resume}
          onTouchStart={pause}
          onTouchEnd={resume}
        >
          <Carousel setApi={setApi} opts={{ loop: true }} className="w-full">
            <div
              className="relative w-full rounded-xl overflow-hidden bg-[#2C2723]"
              style={{ aspectRatio: MEDIA_ASPECT }}
            >
              <CarouselContent className="h-full -ml-0">
                {items.map((item) => (
                  <CarouselItem key={item.id} className="h-full pl-0">
                    <div className="relative w-full h-full flex items-center justify-center bg-[#2C2723]">
                      <FlywheelSlideMedia
                        item={item}
                        onEnlargeImage={() => setEnlargedItem(item)}
                        onVideoPlayingChange={setVideoPlaying}
                      />
                      {item.clickThroughUrl && (
                        <a
                          href={item.clickThroughUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="absolute top-2 right-2 flex items-center gap-1 text-[11px] font-semibold px-2.5 py-1.5 rounded-full bg-white/90 text-[#2C2723] hover:bg-white transition-colors shadow-sm"
                        >
                          Learn more <ExternalLink className="w-3 h-3" />
                        </a>
                      )}
                    </div>
                  </CarouselItem>
                ))}
              </CarouselContent>
              <FlywheelControls api={api} count={items.length} selected={selectedIndex} />
            </div>
          </Carousel>
          {(current?.title || current?.description) && (
            <div className="mt-2.5">
              {current.title && <div className="text-[12.5px] font-bold text-[#2C2723] truncate">{current.title}</div>}
              {current.description && (
                <div className="mt-0.5">
                  <p className={`text-[11px] text-[#9C9285] ${descExpanded ? "" : "line-clamp-2"}`}>{current.description}</p>
                  {descriptionNeedsToggle && (
                    <button
                      type="button"
                      onClick={() => setDescExpanded((v) => !v)}
                      aria-expanded={descExpanded}
                      className="text-[10.5px] font-semibold text-[#E2701F] hover:text-[#C75E12] mt-0.5 transition-colors"
                    >
                      {descExpanded ? "Show less" : "Show more"}
                    </button>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {enlargedItem && (
        <Dialog open={!!enlargedItem} onOpenChange={(open) => { if (!open) setEnlargedItem(null); }}>
          <DialogContent
            showCloseButton
            className="max-w-4xl w-[95vw] sm:w-[90vw] p-3 sm:p-4 bg-black/95 border-none [&_svg]:text-white/80 [&_svg]:hover:text-white"
          >
            <DialogTitle className="text-sm font-medium text-white/80 text-center sm:text-left">{enlargedItem.title}</DialogTitle>
            <img src={enlargedItem.mediaUrl} alt={enlargedItem.title} className="w-full max-h-[80vh] object-contain rounded-lg" />
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
